// ============================================================
// AI Evaluator — Board position evaluation heuristics
// ============================================================

import { type BoardState, type Side, PIECE_VALUES, NUM_PLANES, BOARD_SIZE } from '@core/types';
import { getPieces, findKing } from '@core/board';

/** Evaluate a board state. Positive = white advantage, negative = black advantage. */
export function evaluate(state: BoardState): number {
  let score = 0;

  // Material
  score += materialScore(state);

  // Dimensional control — bonus for presence on multiple planes
  score += dimensionalControl(state, 'white') - dimensionalControl(state, 'black');

  // King safety — penalty for exposed king across dimensions
  score += kingSafety(state, 'white') - kingSafety(state, 'black');

  // Center control per plane
  score += centerControl(state, 'white') - centerControl(state, 'black');

  // Mobility (expensive — only at shallow depths)
  // score += mobility(state, 'white') - mobility(state, 'black');

  return score;
}

function materialScore(state: BoardState): number {
  let score = 0;
  for (let w = 0; w < NUM_PLANES; w++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const piece = state.grid[w][y][x];
        if (piece) {
          const val = PIECE_VALUES[piece.kind];
          score += piece.side === 'white' ? val : -val;
        }
      }
    }
  }
  return score;
}

function dimensionalControl(state: BoardState, side: Side): number {
  const planesOccupied = new Set<number>();
  const pieces = getPieces(state, side);
  for (const { pos } of pieces) {
    planesOccupied.add(pos.w);
  }
  // Bonus for each plane with presence, extra bonus for 3+ planes
  return planesOccupied.size * 15 + (planesOccupied.size >= 3 ? 25 : 0);
}

function kingSafety(state: BoardState, side: Side): number {
  const kingPos = findKing(state, side);
  if (!kingPos) return -9999;

  let safety = 0;
  const pieces = getPieces(state, side);

  // Bonus for friendly pieces near king (same plane)
  for (const { pos } of pieces) {
    if (pos.w === kingPos.w) {
      const dist = Math.abs(pos.x - kingPos.x) + Math.abs(pos.y - kingPos.y);
      if (dist <= 2) safety += 10;
    }
  }

  // Penalty if king is on a plane with few defenders
  const defendersOnPlane = pieces.filter(p => p.pos.w === kingPos.w).length;
  if (defendersOnPlane < 3) safety -= 30;

  return safety;
}

function centerControl(state: BoardState, side: Side): number {
  let score = 0;
  const centerSquares = [
    [3, 3], [3, 4], [4, 3], [4, 4],
    [2, 2], [2, 5], [5, 2], [5, 5],
  ];

  for (let w = 0; w < NUM_PLANES; w++) {
    for (const [x, y] of centerSquares) {
      const piece = state.grid[w][y][x];
      if (piece && piece.side === side) {
        score += x >= 3 && x <= 4 && y >= 3 && y <= 4 ? 12 : 5;
      }
    }
  }
  return score;
}
